import { Grid, Stack } from '@mui/material';
import type { IntegrationProviders } from '@weft/types';
import { INTEGRATION_PROVIDERS } from '@weft/types';
import { validateNotNullOrUndefined, values } from '@weft/utils';
import { HubspotLogoIcon } from 'apps/app/src/components/logo-icons/HubspotLogoIcon';
import { NetSuiteLogoIcon } from 'apps/app/src/components/logo-icons/NetSuiteLogoIcon';
import { QuickbooksLogoIcon } from 'apps/app/src/components/logo-icons/QuickbooksLogoIcon';
import { SalesforceLogoIcon } from 'apps/app/src/components/logo-icons/SalesforceLogoIcon';
import { SlackLogoIcon } from 'apps/app/src/components/logo-icons/SlackLogoIcon';
import { StripeLogoIcon } from 'apps/app/src/components/logo-icons/StripeLogoIcon';
import { VyPageLayout } from 'apps/app/src/components/new-design/layout/VyPageLayout';
import { useConnectedIntegrations } from 'apps/app/src/hooks/requests/use-integrations';
import { useSlackAuthRedirect } from 'apps/app/src/hooks/requests/use-slack';
import { Helmet } from 'react-helmet';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { ConnectNetSuiteModal } from './integrations-page-components/ConnectNetSuiteModal';
import { ConnectSalesforceModal } from './integrations-page-components/ConnectSalesforceModal';
import { ConnectStripeModal } from './integrations-page-components/ConnectStripeModal';
import { IntegrationProviderCard } from './integrations-page-components/IntegrationProviderCard';
import type { IntegrationProviderProperties } from './integrations-page-types/integration-provider-properties';
import { INTEGRATIONS_TYPES } from './integrations-page-types/integrations-types';

const IntegrationsPage: React.FC = () => {
  const navigate = useVyNavigate();
  const { data: connectedIntegrations } = useConnectedIntegrations({ suspense: true });
  validateNotNullOrUndefined(connectedIntegrations, 'connectedIntegrations');

  return (
    <>
      <Helmet>
        <title>Integrations</title>
      </Helmet>
      <VyPageLayout
        title="Integrations"
      >
        <Stack
          maxWidth="lg"
          margin="auto"
          height="100%"
          justifyContent="center"
        >
          <Grid
            container
            spacing={2}
          >
            {values(INTEGRATION_PROVIDERS).map((integrationProvider) => {
              return (
                <Grid
                  item
                  xs={12}
                  lg={4}
                  key={integrationProvider}
                >
                  <IntegrationProviderCard
                    name={integrationProvider}
                    logo={integrationPropertiesByProvider[integrationProvider].logo}
                    connected={connectedIntegrations.includes(integrationProvider)}
                    onProviderCardClick={() => navigate(`/integrations/${integrationProvider}`)}
                    configurationEnabled={integrationPropertiesByProvider[integrationProvider].configurationEnabled}                      
                    integrationType={integrationPropertiesByProvider[integrationProvider].integrationType}
                    connectionEnabled={integrationPropertiesByProvider[integrationProvider].connectionEnabled}
                    getConnectionPortal={integrationPropertiesByProvider[integrationProvider].getConnectionPortal}
                    authRedirect={integrationPropertiesByProvider[integrationProvider].authRedirect}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      </VyPageLayout>
    </>
  );
};

const integrationPropertiesByProvider : Record<IntegrationProviders, IntegrationProviderProperties> = {
  [INTEGRATION_PROVIDERS.netSuite]: {
    logo: <NetSuiteLogoIcon/>,
    getConnectionPortal: (open: boolean, onClose: () => void) => (
      <ConnectNetSuiteModal
        open={open}
        onClose={onClose}
      />
    ),
    connectionEnabled: true,
    configurationEnabled: true,
    integrationType: INTEGRATIONS_TYPES.ERP,
  },
  [INTEGRATION_PROVIDERS.stripe]: {
    logo: <StripeLogoIcon/>,
    getConnectionPortal: (open: boolean, onClose: () => void) => (
      <ConnectStripeModal
        open={open}
        onClose={onClose}
      />
    ),
    connectionEnabled: true,
    configurationEnabled: true,
    integrationType: INTEGRATIONS_TYPES.Payment,
  },
  [INTEGRATION_PROVIDERS.hubspot]: {
    logo: <HubspotLogoIcon/>,
    connectionEnabled: false,
    configurationEnabled: false,
    integrationType: INTEGRATIONS_TYPES.CRM,
  },
  [INTEGRATION_PROVIDERS.salesforce]: {
    logo: <SalesforceLogoIcon/>,
    getConnectionPortal: (open: boolean, onClose: () => void) => (
      <ConnectSalesforceModal
        open={open}
        onClose={onClose}
      />
    ),
    connectionEnabled: true,
    configurationEnabled: false,
    integrationType: INTEGRATIONS_TYPES.CRM,
  },
  [INTEGRATION_PROVIDERS.quickbooks]: {
    logo: <QuickbooksLogoIcon/>,
    connectionEnabled: false,
    configurationEnabled: false,
    integrationType: INTEGRATIONS_TYPES.ERP,
  },
  [INTEGRATION_PROVIDERS.slack]: {
    logo: <SlackLogoIcon/>,
    authRedirect: useSlackAuthRedirect,
    connectionEnabled: true,
    configurationEnabled: false,
    integrationType: INTEGRATIONS_TYPES.Communication,
  },
};

const IntegrationsPageWithSuspenseBoundary = withSuspenseBoundary(IntegrationsPage);
export { IntegrationsPageWithSuspenseBoundary as IntegrationsPage };